/**
 * Statistics — Phase 95 (Feb 2026)
 *
 * Module de statistiques consolidé avec 3 onglets initiaux :
 *  1. Rentabilité machine (Distributeurs AUTOMATIQUE Nayax)
 *  2. Fabrication (OFs terminés — Stock Conthey)
 *  3. Revendeurs B2B (Commandes clients avec reseller_id)
 *
 * Chaque onglet a ses filtres de période + export CSV.
 */
import React, { useEffect, useMemo, useState } from "react";
import SalesMonthlyTab from "../components/stats/SalesMonthlyTab";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";
import { BarChart3, Download, Factory, Loader2, RefreshCw, TrendingUp, Users, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const CHF = (n) => `${(Number(n) || 0).toFixed(2)} CHF`;
const isoDay = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return isoDay(d);
};

// Coloration marge : rouge <0, orange <10%, vert >20%, gris sinon
const marginBadgeClass = (pct) => {
  if (pct < 0) return "bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]";
  if (pct < 10) return "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]";
  if (pct >= 20) return "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]";
  return "bg-[#F4F4F0] text-[#575A57] border-[#C8C8C0]";
};

function DateRangeFilter({ dateFrom, setDateFrom, dateTo, setDateTo, onQuickRange, testidPrefix }) {
  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-[#575A57] mb-1 block">Du</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid={`${testidPrefix}-from`} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#575A57] mb-1 block">Au</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid={`${testidPrefix}-to`} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => onQuickRange(7)} data-testid={`${testidPrefix}-range-7d`}>7 jours</Button>
          <Button size="sm" variant="outline" onClick={() => onQuickRange(30)} data-testid={`${testidPrefix}-range-30d`}>30 jours</Button>
          <Button size="sm" variant="outline" onClick={() => onQuickRange(90)} data-testid={`${testidPrefix}-range-90d`}>90 jours</Button>
          <Button size="sm" variant="outline" onClick={() => onQuickRange(365)} data-testid={`${testidPrefix}-range-365d`}>1 an</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------------------------------------------------
// Onglet 1 — Rentabilité par distributeur
// -------------------------------------------------------------
function ProfitabilityTab() {
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(isoDay(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/statistics/distributor-profitability", { params: { date_from: dateFrom, date_to: dateTo } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement rentabilité");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const r = await api.get(`/statistics/distributor-profitability/export.csv?${params}`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data]));
      link.download = `rentabilite-distributeurs-${isoDay(new Date())}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error("Erreur export CSV");
    }
  };

  const items = data?.items || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="stats-profitability-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-[#575A57]">
          Formule : <span className="font-mono">CA − Coût matière − Pertes valorisées − Part partenaire estim.</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="stats-profit-reload">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Rafraîchir
          </Button>
          <Button size="sm" onClick={handleExport} disabled={loading || items.length === 0} data-testid="stats-profit-export" className="bg-[#1F4E3D] hover:bg-[#173D30] text-white">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <DateRangeFilter
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        onQuickRange={(n) => { setDateFrom(daysAgo(n)); setDateTo(isoDay(new Date())); }}
        testidPrefix="stats-profit"
      />

      {/* KPI Totaux */}
      <div className="grid gap-3 md:grid-cols-5">
        <Card data-testid="stats-profit-kpi-revenue">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">CA total</p>
            <p className="text-2xl font-semibold text-[#1F4E3D] mt-1">{CHF(totals.revenue)}</p>
          </CardContent>
        </Card>
        <Card data-testid="stats-profit-kpi-cogs">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Coût matière</p>
            <p className="text-2xl font-semibold text-[#8C6B2F] mt-1">{CHF(totals.cogs)}</p>
          </CardContent>
        </Card>
        <Card data-testid="stats-profit-kpi-losses">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Pertes (valeur)</p>
            <p className="text-2xl font-semibold text-[#A04E2C] mt-1">{CHF(totals.losses_value)}</p>
          </CardContent>
        </Card>
        <Card data-testid="stats-profit-kpi-partner">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Part partenaire (est.)</p>
            <p className="text-2xl font-semibold text-[#5847A6] mt-1">{CHF(totals.partner_share_estimated)}</p>
          </CardContent>
        </Card>
        <Card data-testid="stats-profit-kpi-margin">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Marge nette</p>
            <p className={`text-2xl font-semibold mt-1 ${(totals.net_margin || 0) < 0 ? "text-[#A04E2C]" : "text-[#1F4E3D]"}`}>
              {CHF(totals.net_margin)}
            </p>
            <p className="text-xs text-[#575A57] mt-1">{fmtNum(totals.margin_percent)} % du CA</p>
          </CardContent>
        </Card>
      </div>

      {/* Tableau détaillé */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12 text-[#575A57]"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Chargement…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#575A57]" data-testid="stats-profit-empty">
              Aucun distributeur AUTOMATIQUE trouvé sur cette période.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="stats-profit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Distributeur</TableHead>
                    <TableHead className="text-right">Ventes</TableHead>
                    <TableHead className="text-right">CA</TableHead>
                    <TableHead className="text-right">Coût matière</TableHead>
                    <TableHead className="text-right">Pertes</TableHead>
                    <TableHead className="text-right">Part partenaire</TableHead>
                    <TableHead className="text-right">Marge nette</TableHead>
                    <TableHead className="text-right">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.distributor_id} data-testid={`stats-profit-row-${it.distributor_id}`}>
                      <TableCell>
                        <div className="font-medium">{it.distributor_name}</div>
                        {it.distributor_city && <div className="text-xs text-[#575A57]">{it.distributor_city}</div>}
                      </TableCell>
                      <TableCell className="text-right text-sm">{fmtNum(it.sales_qty)}</TableCell>
                      <TableCell className="text-right font-medium">{CHF(it.revenue)}</TableCell>
                      <TableCell className="text-right text-[#8C6B2F]">{CHF(it.cogs)}</TableCell>
                      <TableCell className="text-right text-[#A04E2C]">{CHF(it.losses_value)}</TableCell>
                      <TableCell className="text-right text-[#5847A6]">
                        {CHF(it.partner_share_estimated)}
                        {it.commission_rate > 0 && <span className="text-xs ml-1 text-[#575A57]">({fmtNum(it.commission_rate)}%)</span>}
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${it.net_margin < 0 ? "text-[#A04E2C]" : "text-[#1F4E3D]"}`}>
                        {CHF(it.net_margin)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className={`text-xs ${marginBadgeClass(it.margin_percent)}`}>{fmtNum(it.margin_percent)}%</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#F8F5EE] border-[#E8D3A8]">
        <CardContent className="pt-6 text-sm text-[#575A57]">
          <p className="font-medium text-[#8C6B2F] mb-1">Note sur la part partenaire</p>
          <p>La colonne « Part partenaire » est <span className="font-medium">estimative</span> : elle applique le <span className="font-mono">commission_rate</span> saisi sur chaque partenaire au CA du distributeur assigné. Le calcul officiel reste sous Excel.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// -------------------------------------------------------------
// Onglet 2 — Fabrication
// -------------------------------------------------------------
function ProductionTab() {
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(isoDay(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/statistics/production", { params: { date_from: dateFrom, date_to: dateTo } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement fabrication");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const r = await api.get(`/statistics/production/export.csv?${params}`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data]));
      link.download = `fabrication-${isoDay(new Date())}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export CSV téléchargé");
    } catch { toast.error("Erreur export CSV"); }
  };

  const items = data?.items || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="stats-production-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-[#575A57]">
          Base : OFs en statut <span className="font-mono">TERMINE</span> sur la période (date de production réelle).
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="stats-prod-reload">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Rafraîchir
          </Button>
          <Button size="sm" onClick={handleExport} disabled={loading || items.length === 0} data-testid="stats-prod-export" className="bg-[#1F4E3D] hover:bg-[#173D30] text-white">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <DateRangeFilter
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        onQuickRange={(n) => { setDateFrom(daysAgo(n)); setDateTo(isoDay(new Date())); }}
        testidPrefix="stats-prod"
      />

      <div className="grid gap-3 md:grid-cols-4">
        <Card data-testid="stats-prod-kpi-orders">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">OFs terminés</p>
            <p className="text-2xl font-semibold text-[#1F2F1F] mt-1">{totals.total_orders || 0}</p>
          </CardContent>
        </Card>
        <Card data-testid="stats-prod-kpi-qty">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Barquettes produites</p>
            <p className="text-2xl font-semibold text-[#1F4E3D] mt-1">{fmtNum(totals.qty_produced)}</p>
            <p className="text-xs text-[#575A57] mt-1">Cible : {fmtNum(totals.qty_target)}</p>
          </CardContent>
        </Card>
        <Card data-testid="stats-prod-kpi-yield">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Rendement</p>
            <p className="text-2xl font-semibold text-[#8C6B2F] mt-1">{fmtNum(totals.yield_percent)} %</p>
            <p className="text-xs text-[#575A57] mt-1">Produit / cible</p>
          </CardContent>
        </Card>
        <Card data-testid="stats-prod-kpi-cost">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Coût matière</p>
            <p className="text-2xl font-semibold text-[#5847A6] mt-1">{CHF(totals.material_cost)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12 text-[#575A57]"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Chargement…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#575A57]" data-testid="stats-prod-empty">
              Aucun OF terminé sur cette période.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="stats-prod-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead className="text-right">OFs</TableHead>
                    <TableHead className="text-right">Cible</TableHead>
                    <TableHead className="text-right">Produit</TableHead>
                    <TableHead className="text-right">Rendement</TableHead>
                    <TableHead className="text-right">Coût u.</TableHead>
                    <TableHead className="text-right">Coût matière total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.product_id} data-testid={`stats-prod-row-${it.product_id}`}>
                      <TableCell>
                        <div className="font-medium">{it.product_name}</div>
                        {it.product_sku && <div className="text-xs text-[#575A57]">{it.product_sku}</div>}
                      </TableCell>
                      <TableCell className="text-right">{it.of_count}</TableCell>
                      <TableCell className="text-right text-sm">{fmtNum(it.qty_target)}</TableCell>
                      <TableCell className="text-right font-medium">{fmtNum(it.qty_produced)}</TableCell>
                      <TableCell className="text-right text-sm">{fmtNum(it.yield_percent)} %</TableCell>
                      <TableCell className="text-right text-sm">{it.unit_cost ? `${it.unit_cost.toFixed(4)} CHF` : "—"}</TableCell>
                      <TableCell className="text-right font-semibold text-[#5847A6]">{CHF(it.material_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// -------------------------------------------------------------
// Onglet 3 — Revendeurs B2B
// -------------------------------------------------------------
function ResellersTab() {
  const [dateFrom, setDateFrom] = useState(daysAgo(90));
  const [dateTo, setDateTo] = useState(isoDay(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);   // Phase 101ea.4

  const load = async () => {
    setLoading(true);
    try {
      const [r, orphanRes] = await Promise.all([
        api.get("/statistics/resellers", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/client-orders/without-reseller?limit=1").catch(() => ({ data: { count: 0 } })),
      ]);
      setData(r.data);
      setOrphanCount(orphanRes.data?.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement revendeurs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const r = await api.get(`/statistics/resellers/export.csv?${params}`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data]));
      link.download = `revendeurs-${isoDay(new Date())}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export CSV téléchargé");
    } catch { toast.error("Erreur export CSV"); }
  };

  const items = data?.items || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="stats-resellers-tab">
      {/* Phase 101ea.4 — Bandeau CC orphelines */}
      {orphanCount > 0 && (
        <Card className="border-[#B68A3E] bg-[#FDF7E8]" data-testid="stats-resellers-orphan-banner">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 text-sm">
              <span className="font-semibold text-[#8C6B2F]">⚠️ {orphanCount} commande{orphanCount > 1 ? "s" : ""} client{orphanCount > 1 ? "s" : ""} sans revendeur assigné</span>
              <div className="text-xs text-[#575A57] mt-0.5">
                Ces commandes n'apparaissent PAS dans les stats ci-dessous. Rattache-les pour corriger le CA revendeur.
              </div>
            </div>
            <Link to="/commandes-orphelines" data-testid="stats-resellers-orphan-link">
              <Button size="sm" className="bg-[#8C6B2F] hover:bg-[#725520] text-white">
                Rattacher maintenant →
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-[#575A57]">
          Base : commandes clients avec <span className="font-mono">reseller_id</span> non nul, statut ≥ VALIDÉE.
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="stats-resellers-reload">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Rafraîchir
          </Button>
          <Button size="sm" onClick={handleExport} disabled={loading || items.length === 0} data-testid="stats-resellers-export" className="bg-[#1F4E3D] hover:bg-[#173D30] text-white">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <DateRangeFilter
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        onQuickRange={(n) => { setDateFrom(daysAgo(n)); setDateTo(isoDay(new Date())); }}
        testidPrefix="stats-resellers"
      />

      <div className="grid gap-3 md:grid-cols-4">
        <Card data-testid="stats-resellers-kpi-count"><CardContent className="pt-6">
          <p className="text-xs text-[#575A57] uppercase tracking-wide">Revendeurs actifs</p>
          <p className="text-2xl font-semibold text-[#1F2F1F] mt-1">{totals.unique_resellers || 0}</p>
        </CardContent></Card>
        <Card data-testid="stats-resellers-kpi-orders"><CardContent className="pt-6">
          <p className="text-xs text-[#575A57] uppercase tracking-wide">Commandes</p>
          <p className="text-2xl font-semibold text-[#1F2F1F] mt-1">{totals.orders_count || 0}</p>
        </CardContent></Card>
        <Card data-testid="stats-resellers-kpi-revenue"><CardContent className="pt-6">
          <p className="text-xs text-[#575A57] uppercase tracking-wide">CA total</p>
          <p className="text-2xl font-semibold text-[#1F4E3D] mt-1">{CHF(totals.revenue)}</p>
        </CardContent></Card>
        <Card data-testid="stats-resellers-kpi-basket"><CardContent className="pt-6">
          <p className="text-xs text-[#575A57] uppercase tracking-wide">Panier moyen</p>
          <p className="text-2xl font-semibold text-[#8C6B2F] mt-1">{CHF(totals.avg_basket)}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12 text-[#575A57]"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Chargement…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#575A57]" data-testid="stats-resellers-empty">
              Aucune commande revendeur sur cette période.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="stats-resellers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Revendeur</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Commandes</TableHead>
                    <TableHead className="text-right">CA</TableHead>
                    <TableHead className="text-right">Panier moyen</TableHead>
                    <TableHead>Dernière commande</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.reseller_id} data-testid={`stats-resellers-row-${it.reseller_id}`}>
                      <TableCell>
                        <div className="font-medium">{it.reseller_name}</div>
                        {it.reseller_city && <div className="text-xs text-[#575A57]">{it.reseller_city}</div>}
                      </TableCell>
                      <TableCell className="text-sm text-[#575A57]">{it.reseller_type || "—"}</TableCell>
                      <TableCell className="text-right">{it.orders_count}</TableCell>
                      <TableCell className="text-right font-semibold text-[#1F4E3D]">{CHF(it.revenue)}</TableCell>
                      <TableCell className="text-right text-sm">{CHF(it.avg_basket)}</TableCell>
                      <TableCell className="text-sm">{it.last_order_at ? fmtDate(it.last_order_at) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// -------------------------------------------------------------
// Page principale
// -------------------------------------------------------------
export default function Statistics() {
  const [tab, setTab] = useState("sales-monthly");

  return (
    <div className="space-y-6" data-testid="statistics-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#1F2F1F] flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#1F4E3D]" />
            Statistiques
          </h1>
          <p className="text-sm text-[#575A57] mt-1">
            Vue consolidée des performances : rentabilité machine, fabrication, revendeurs B2B.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await api.get("/products-cost-report/pdf", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 30_000);
              } catch (e) {
                toast.error("Erreur export PDF prix de revient");
              }
            }}
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
            data-testid="stats-cost-report-pdf-btn"
          >
            <Factory className="w-4 h-4 mr-1" />
            Prix de revient PDF
          </Button>
          <Link to="/statistiques/nayax" data-testid="stats-nayax-link">
            <Button className="bg-[#8C6B2F] hover:bg-[#725520] text-white">
              <ShoppingCart className="w-4 h-4 mr-1" />
              Rapport Ventes Nayax (mensuel + Best-Sellers)
            </Button>
          </Link>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-3xl">
          <TabsTrigger value="sales-monthly" data-testid="stats-tab-sales-monthly">
            <ShoppingCart className="w-4 h-4 mr-1" />
            Ventes par mois
          </TabsTrigger>
          <TabsTrigger value="profitability" data-testid="stats-tab-profitability">
            <TrendingUp className="w-4 h-4 mr-1" />
            Rentabilité machine
          </TabsTrigger>
          <TabsTrigger value="production" data-testid="stats-tab-production">
            <Factory className="w-4 h-4 mr-1" />
            Fabrication
          </TabsTrigger>
          <TabsTrigger value="resellers" data-testid="stats-tab-resellers">
            <Users className="w-4 h-4 mr-1" />
            Revendeurs B2B
          </TabsTrigger>
        </TabsList>
        <TabsContent value="sales-monthly" className="mt-4">
          <SalesMonthlyTab />
        </TabsContent>
        <TabsContent value="profitability" className="mt-4">
          <ProfitabilityTab />
        </TabsContent>
        <TabsContent value="production" className="mt-4">
          <ProductionTab />
        </TabsContent>
        <TabsContent value="resellers" className="mt-4">
          <ResellersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
